// @flow
import { get } from 'lodash';
import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
    defineMessages, injectIntl, IntlShape,
} from 'react-intl';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import type { Confirmation } from 'speed-js-react';
import { withConfirmation, connectAPI } from 'speed-js-react';
import * as sidePanelActions from 'esm-web-pro/redux/side-panel/actions';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import MainActions from 'esm-web-pro/components/layout/sidePanel/MainActions';
import DeleteIcon from 'esm-web-pro/components/layout/sidePanel/icons/DeleteIcon';
import EditIcon from 'esm-web-pro/components/layout/sidePanel/icons/EditIcon';
import SidePane from 'esm-web-pro/components/layout/SidePane';
import { ApiFormWithModel } from 'esm-web-pro/containers/forms/FormWithModel';


type SidePanelActionProps = {
    openPanel: (key: string, url: ?string, sensitivity?: boolean) => { key: string, url: string },
    closePanel: (key: string, skipSensitivity: boolean) => { key: string },
}

type Props = {
    ...$Exact<SidePanelActionProps>,
    asset: Object,
    onEdit: Function,
    confirm: (confirmation: Confirmation) => void,
    intl: IntlShape,
    history: Object,
    match: Object,
    map: Object,
    deleteObject?: Object,
    intl: IntlShape,
    editor: Object,
}

const msg = defineMessages({
    deleteTitle: {
        id: 'delete-asset-title',
        defaultMessage: 'Are you sure you want to delete the asset?',
    },
    cancelDelete: {
        id: 'delete-asset-cancel',
        defaultMessage: 'Cancel',
    },
    confirmDelete: {
        id: 'delete-asset-confirm',
        defaultMessage: 'Confirm',
    },
    title: {
        id: 'asset-editor-title',
        defaultMessage: 'Edit asset',
    },
});

const ASSET_EDIT_KEY = 'ASSET_EDIT_KEY';

const AssetActions = (props: Props) => {
    const {
        asset, map: { is_draft: isDraft, uuid: mapUuid }, onEdit, confirm, intl: { formatMessage }, deleteObject,
        history, match, openPanel, closePanel, editor,
    } = props;

    const handleClose = (skipSensitivity = false) => {
        closePanel(ASSET_EDIT_KEY, skipSensitivity);
    };

    const handleSave = (newValues) => {
        onEdit(newValues);
        handleClose(true);
    };

    const actions = [];

    const disallowDeleteConfirm = (message) => confirm({
        message,
        actions: {
            cancel: {
                label: formatMessage(msg.cancelDelete),
                callback: () => {
                },
                type: 'negative',
            },
        },
    });

    const allowDeleteConfirm = () => confirm({
        message: formatMessage(msg.deleteTitle),
        actions: {
            cancel: {
                label: formatMessage(msg.cancelDelete),
                callback: () => {},
                type: 'negative',
            },
            confirm: {
                label: formatMessage(msg.confirmDelete),
                callback: () => {
                    if (deleteObject) {
                        deleteObject(asset.uuid, 'pop.asset', [asset.container.uuid]).then((res) => {
                            if (res.data && res.data.error) {
                                disallowDeleteConfirm(res.data.error);
                            } else {
                                history.push(
                                    `${match.url.split('/layer')[0]}/layer/${
                                        asset.container.type}/${asset.container.identity}`,
                                );
                            }
                        });
                    }
                },
                type: 'positive',
            },
        },
    });

    if (isDraft) {
        actions.push(
            {
                key: `${asset.uuid}-edit`,
                icon: <EditIcon />,
                callback: () => openPanel(ASSET_EDIT_KEY, '', match.url, true),
                tooltip: formatMessage(actionMessages.edit),
                disabled: editor?.state.loading,
                marginVertical: '15px',
                radius: '0%',
                color: 'transparent',
            },
        );

        actions.push({
            key: `${asset.uuid}-delete`,
            icon: <DeleteIcon />,
            callback: () => allowDeleteConfirm(),
            tooltip: formatMessage(actionMessages.delete),
            disabled: editor?.state.loading,
            marginVertical: '15px',
            radius: '0%',
            color: 'transparent',
        });
    }

    return (
        <>
            <SidePane
                id={ASSET_EDIT_KEY}
                key={asset.uuid}
                title={`${formatMessage(msg.title)} ${asset.ref}`}
            >
                <ApiFormWithModel
                    objectType={asset.asset_type}
                    objectId={asset.uuid}
                    resourceName="pop.assetForType"
                    resourceArguments={[asset.container.uuid, asset.asset_type]}
                    excludeFields={['infrastructure_provider', 'container', 'pop', 'map', 'stage', 'ref']}
                    height="100%"
                    initial={{}}
                    onClose={() => handleClose()}
                    onSave={handleSave}
                />
            </SidePane>
            <MainActions main actions={actions} />
        </>
    );
};

AssetActions.defaultProps = {
    deleteObject: undefined,
};

const apiCallback = (apiClient: ApiClient) => ({
    deleteObject: {
        send: (id, resourceName, resourceArguments = []) => {
            const resource = get(apiClient.resources, resourceName);
            return resource.delete(...resourceArguments, id);
        },
    },
});

const mapDispatchToProps = (dispatch) => (
    bindActionCreators(sidePanelActions, dispatch)
);

export default connectAPI(apiCallback)(
    withRouter(
        withConfirmation(
            injectIntl(
                connect(null, mapDispatchToProps)(AssetActions),
            ),
        ),
    ),
);
